import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  BellOff,
  BellRing,
  Check,
  Copy,
  KeyRound,
  Link as LinkIcon,
  Plus,
  RefreshCw,
  Send,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Trash2,
  X,
} from 'lucide-react';
import {
  ApiError,
  api,
  type Player,
  type PushSubscriberRow,
  type Rank,
  type SelfReportLinkRow,
  type SelfReportPendingRow,
} from '@/lib/api';
import { useSelfReportStream } from '@/lib/useSelfReportStream';
import {
  disablePush,
  enablePush,
  getPushStatus,
  isPushSupported,
  type PushStatus,
} from '@/lib/pushClient';

export function selfReportUrl(token: string) {
  if (typeof window === 'undefined') return `/self-report/${token}`;
  return `${window.location.origin}/self-report/${token}`;
}

export function playerHubUrl(token: string) {
  if (typeof window === 'undefined') return `/players/${token}/self-report`;
  return `${window.location.origin}/players/${token}/self-report`;
}

const NOTIFY_PREF_KEY = 'self-report-notify';

function readNotifyPref(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(NOTIFY_PREF_KEY) !== 'off';
}

function writeNotifyPref(on: boolean) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(NOTIFY_PREF_KEY, on ? 'on' : 'off');
}

type NotificationPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

function getPermission(): NotificationPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return window.Notification.permission as NotificationPermissionState;
}

export function PendingSelfReportsSection() {
  const qc = useQueryClient();
  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    getPermission(),
  );
  const [notifyOn, setNotifyOn] = useState<boolean>(() => readNotifyPref());

  const query = useQuery({
    queryKey: ['self-reports', 'pending'],
    queryFn: api.listPendingSelfReports,
  });

  const notificationsActive = permission === 'granted' && notifyOn;

  useSelfReportStream({
    enabled: true,
    onCreated: (payload) => {
      qc.invalidateQueries({ queryKey: ['self-reports', 'pending'] });
      qc.invalidateQueries({ queryKey: ['self-report-links'] });
      if (notificationsActive && typeof window !== 'undefined') {
        try {
          new window.Notification('New self-report', {
            body: `${payload.playerName}${payload.autoApproved ? ' (trusted)' : ''}`,
            tag: `self-report-${payload.id}`,
            icon: '/favicon.svg',
          });
        } catch {
          // Notification constructor can throw on some platforms — ignore.
        }
      }
    },
  });

  const requestPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    try {
      const result = await window.Notification.requestPermission();
      setPermission(result as NotificationPermissionState);
      if (result === 'granted') {
        setNotifyOn(true);
        writeNotifyPref(true);
      }
    } catch {
      // ignore
    }
  };

  const toggleNotifyPref = () => {
    const next = !notifyOn;
    setNotifyOn(next);
    writeNotifyPref(next);
  };

  const invalidateAfterMutation = () => {
    qc.invalidateQueries({ queryKey: ['self-reports', 'pending'] });
    qc.invalidateQueries({ queryKey: ['strength-requests'] });
    qc.invalidateQueries({ queryKey: ['strength-request'] });
    qc.invalidateQueries({ queryKey: ['snapshots'] });
  };
  const approve = useMutation({
    mutationFn: (id: number) => api.approveSelfReport(id),
    onSuccess: invalidateAfterMutation,
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to approve'),
  });
  const reject = useMutation({
    mutationFn: (id: number) => api.rejectSelfReport(id),
    onSuccess: invalidateAfterMutation,
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to reject'),
  });
  const bulk = useMutation({
    mutationFn: (args: { action: 'approve' | 'reject'; ids: number[] }) =>
      api.bulkSelfReports(args.action, args.ids),
    onSuccess: invalidateAfterMutation,
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to process reports'),
  });
  const rows = query.data?.rows ?? [];
  const grouped = useMemo(() => {
    const map = new Map<number, SelfReportPendingRow[]>();
    for (const r of rows) {
      const list = map.get(r.player.id) ?? [];
      list.push(r);
      map.set(r.player.id, list);
    }
    return [...map.values()].sort((a, b) =>
      a[0].submittedAt < b[0].submittedAt ? 1 : -1,
    );
  }, [rows]);
  const busy = approve.isPending || reject.isPending || bulk.isPending;
  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          Pending self-reports
          <span className="ml-1 rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-300">
            {rows.length}
          </span>
        </h2>
        <NotificationControls
          permission={permission}
          notifyOn={notifyOn}
          onRequest={requestPermission}
          onToggle={toggleNotifyPref}
        />
        <PushControls />
      </div>
      {query.isLoading && (
        <div className="text-sm text-slate-400">Loading…</div>
      )}
      {!query.isLoading && rows.length === 0 && (
        <div className="text-sm text-slate-500">
          No self-reports waiting for review.
        </div>
      )}
      <ul className="space-y-3">
        {grouped.map((group) => (
          <PendingGroup
            key={group[0].player.id}
            rows={group}
            busy={busy}
            onApprove={(id) => approve.mutate(id)}
            onReject={(id) => reject.mutate(id)}
            onBulk={(action, ids) => bulk.mutate({ action, ids })}
          />
        ))}
      </ul>
    </section>
  );
}

function PendingGroup({
  rows,
  busy,
  onApprove,
  onReject,
  onBulk,
}: {
  rows: SelfReportPendingRow[];
  busy: boolean;
  onApprove: (id: number) => void;
  onReject: (id: number) => void;
  onBulk: (action: 'approve' | 'reject', ids: number[]) => void;
}) {
  const first = rows[0];
  const name = first.player.displayName?.trim() || first.player.name;
  const many = rows.length > 1;
  return (
    <li className="rounded border border-slate-800 bg-slate-950/30 p-2">
      {many && (
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{name}</span>
            <span className="text-xs text-slate-500">
              · {first.player.rank}
            </span>
            <span className="rounded bg-amber-900/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">
              {rows.length} pending
            </span>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                onBulk(
                  'approve',
                  rows.map((r) => r.id),
                )
              }
              className="flex items-center gap-1 rounded bg-emerald-700 px-3 py-1.5 text-xs hover:bg-emerald-600 disabled:opacity-50"
            >
              <Check size={12} /> Approve all
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (
                  confirm(
                    `Reject all ${rows.length} pending reports from ${name}?`,
                  )
                )
                  onBulk(
                    'reject',
                    rows.map((r) => r.id),
                  );
              }}
              className="flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800 disabled:opacity-50"
            >
              <X size={12} /> Reject all
            </button>
          </div>
        </div>
      )}
      <ul className="space-y-2">
        {rows.map((row) => (
          <PendingRow
            key={row.id}
            row={row}
            onApprove={() => onApprove(row.id)}
            onReject={() => onReject(row.id)}
            busy={busy}
          />
        ))}
      </ul>
    </li>
  );
}

function NotificationControls({
  permission,
  notifyOn,
  onRequest,
  onToggle,
}: {
  permission: NotificationPermissionState;
  notifyOn: boolean;
  onRequest: () => void;
  onToggle: () => void;
}) {
  if (permission === 'unsupported') {
    return (
      <span className="ml-auto flex items-center gap-1 text-xs text-slate-500">
        <BellOff size={12} /> Notifications not supported
      </span>
    );
  }
  if (permission === 'denied') {
    return (
      <span className="ml-auto flex items-center gap-1 text-xs text-slate-500">
        <BellOff size={12} /> Notifications blocked in browser
      </span>
    );
  }
  if (permission === 'default') {
    return (
      <button
        type="button"
        onClick={onRequest}
        className="ml-auto flex items-center gap-1 rounded border border-indigo-700 bg-indigo-900/30 px-2 py-1 text-xs text-indigo-200 hover:bg-indigo-900/60"
      >
        <Bell size={12} /> Enable notifications
      </button>
    );
  }
  // granted
  return (
    <button
      type="button"
      onClick={onToggle}
      title={
        notifyOn
          ? 'Notifications on — click to mute in this browser'
          : 'Notifications muted here — click to enable'
      }
      className={
        'ml-auto flex items-center gap-1 rounded border px-2 py-1 text-xs ' +
        (notifyOn
          ? 'border-emerald-900/60 bg-emerald-950/40 text-emerald-200'
          : 'border-slate-700 text-slate-400 hover:bg-slate-800')
      }
    >
      {notifyOn ? (
        <>
          <BellRing size={12} /> Notifications on
        </>
      ) : (
        <>
          <BellOff size={12} /> Notifications muted
        </>
      )}
    </button>
  );
}

function PushControls() {
  const [status, setStatus] = useState<PushStatus | 'loading'>('loading');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }
    let cancelled = false;
    getPushStatus().then((s) => {
      if (!cancelled) setStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onEnable = async () => {
    setBusy(true);
    try {
      const next = await enablePush();
      setStatus(next);
      if (next === 'not-configured') {
        alert('Push notifications are not configured on the server yet.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to enable push');
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      const next = await disablePush();
      setStatus(next);
    } finally {
      setBusy(false);
    }
  };

  if (status === 'loading') return null;
  if (status === 'unsupported') return null;
  if (status === 'denied') {
    return (
      <span className="flex items-center gap-1 text-xs text-slate-500">
        <BellOff size={12} /> Push blocked
      </span>
    );
  }
  if (status === 'not-configured') {
    return (
      <span className="flex items-center gap-1 text-xs text-slate-500">
        <BellOff size={12} /> Push not configured
      </span>
    );
  }
  if (status === 'subscribed') {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={onDisable}
        title="This device receives push notifications — click to disable"
        className="flex items-center gap-1 rounded border border-emerald-900/60 bg-emerald-950/40 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
      >
        <BellRing size={12} /> Push on
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onEnable}
      title="Get notified even when the tab is closed"
      className="flex items-center gap-1 rounded border border-indigo-700 bg-indigo-900/30 px-2 py-1 text-xs text-indigo-200 hover:bg-indigo-900/60 disabled:opacity-50"
    >
      <Bell size={12} /> Enable push
    </button>
  );
}

function PendingRow({
  row,
  onApprove,
  onReject,
  busy,
}: {
  row: SelfReportPendingRow;
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
}) {
  const prevSum = row.latestApproved
    ? row.latestApproved.m1Power + row.latestApproved.m2Power + row.latestApproved.m3Power
    : null;
  const newSum = row.values.m1Power + row.values.m2Power + row.values.m3Power;
  const delta = prevSum != null ? newSum - prevSum : null;
  const name = row.player.displayName?.trim() || row.player.name;
  return (
    <li className="rounded border border-slate-800 bg-slate-950/40 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">
            {name}{' '}
            <span className="text-xs text-slate-500">· {row.player.rank}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Submitted {row.submittedAt.slice(0, 16).replace('T', ' ')}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
            <MPreview label="M1" power={row.values.m1Power} type={row.values.m1Type} prev={row.latestApproved?.m1Power ?? null} />
            <MPreview label="M2" power={row.values.m2Power} type={row.values.m2Type} prev={row.latestApproved?.m2Power ?? null} />
            <MPreview label="M3" power={row.values.m3Power} type={row.values.m3Type} prev={row.latestApproved?.m3Power ?? null} />
          </div>
          <div className="mt-2 text-xs text-slate-400">
            Σ new{' '}
            <span className="tabular-nums text-slate-200">{newSum.toFixed(2)}</span>
            {prevSum != null && (
              <>
                {' '}· Σ prev{' '}
                <span className="tabular-nums">{prevSum.toFixed(2)}</span>
                {' '}·{' '}
                <span
                  className={
                    'tabular-nums ' +
                    ((delta ?? 0) >= 0 ? 'text-emerald-300' : 'text-amber-300')
                  }
                >
                  Δ {(delta ?? 0).toFixed(2)}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onApprove}
            className="flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs hover:bg-emerald-500 disabled:opacity-50"
          >
            <Check size={12} /> Approve
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (confirm('Reject this self-report?')) onReject();
            }}
            className="flex items-center gap-1 rounded border border-slate-700 px-3 py-1.5 text-xs hover:bg-slate-800 disabled:opacity-50"
          >
            <X size={12} /> Reject
          </button>
        </div>
      </div>
    </li>
  );
}

function MPreview({
  label,
  power,
  type,
  prev,
}: {
  label: string;
  power: number;
  type: string;
  prev: number | null;
}) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/40 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div className="tabular-nums text-slate-100">{power.toFixed(2)}</div>
      <div className="text-[10px] text-slate-500">
        {type}
        {prev != null && (
          <span className="ml-1 text-slate-600">← {prev.toFixed(2)}</span>
        )}
      </div>
    </div>
  );
}

const RANK_ORDER: Record<Rank, number> = { R5: 5, R4: 4, R3: 3, R2: 2, R1: 1 };

interface RosterRow {
  player: Player;
  link: SelfReportLinkRow | null;
}

export function MagicLinksSection() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pushTarget, setPushTarget] = useState<
    | { kind: 'player'; playerId: number; label: string }
    | { kind: 'bulk'; playerIds: number[] }
    | null
  >(null);

  const linksQuery = useQuery({
    queryKey: ['self-report-links'],
    queryFn: api.listSelfReportLinks,
  });
  const playersQuery = useQuery<Player[]>({
    queryKey: ['players'],
    queryFn: () => api.listPlayers(),
  });
  const createLink = useMutation({
    mutationFn: (playerId: number) => api.createSelfReportToken(playerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['self-report-links'] }),
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to create link'),
  });
  const deleteLink = useMutation({
    mutationFn: (playerId: number) => api.deleteSelfReportToken(playerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['self-report-links'] }),
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to revoke link'),
  });
  const linksById = useMemo(() => {
    const map = new Map<number, SelfReportLinkRow>();
    for (const link of linksQuery.data?.rows ?? []) map.set(link.id, link);
    return map;
  }, [linksQuery.data]);

  const rosterRows: RosterRow[] = useMemo(() => {
    const players = (playersQuery.data ?? []).filter((p) => p.active);
    return players
      .map((player) => ({ player, link: linksById.get(player.id) ?? null }))
      .sort((a, b) => {
        const rankDiff =
          RANK_ORDER[b.player.rank] - RANK_ORDER[a.player.rank];
        if (rankDiff !== 0) return rankDiff;
        const nameA = (a.player.displayName?.trim() || a.player.name).toLowerCase();
        const nameB = (b.player.displayName?.trim() || b.player.name).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [playersQuery.data, linksById]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rosterRows.filter((row) => {
      if (showOnlyMissing && row.link) return false;
      if (!term) return true;
      return (
        row.player.name.toLowerCase().includes(term) ||
        (row.player.displayName ?? '').toLowerCase().includes(term)
      );
    });
  }, [rosterRows, search, showOnlyMissing]);

  const linkCount = rosterRows.filter((r) => r.link).length;
  const totalCount = rosterRows.length;
  const missingCount = totalCount - linkCount;
  const busy = createLink.isPending || deleteLink.isPending;
  const pushablePlayerIds = useMemo(
    () =>
      rosterRows
        .filter((r) => (r.link?.pushDeviceCount ?? 0) > 0)
        .map((r) => r.player.id),
    [rosterRows],
  );
  const selectedPushablePlayerIds = useMemo(
    () => pushablePlayerIds.filter((id) => selected.has(id)),
    [pushablePlayerIds, selected],
  );

  const toggleSelected = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllPushable = () => setSelected(new Set(pushablePlayerIds));
  const clearSelection = () => setSelected(new Set());

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          <KeyRound size={14} /> Magic links
        </h2>
        <span className="rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-300">
          {linkCount}/{totalCount}
        </span>
        {missingCount > 0 && (
          <span className="text-xs text-slate-500">
            {missingCount} without link
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {selected.size > 0 ? (
            <>
              <span className="text-xs text-slate-400">
                {selectedPushablePlayerIds.length}/{selected.size} pushable
              </span>
              <button
                type="button"
                onClick={clearSelection}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                Clear
              </button>
              <button
                type="button"
                disabled={selectedPushablePlayerIds.length === 0}
                onClick={() =>
                  setPushTarget({
                    kind: 'bulk',
                    playerIds: selectedPushablePlayerIds,
                  })
                }
                className="flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                <Send size={12} /> Send push to {selectedPushablePlayerIds.length}
              </button>
            </>
          ) : (
            pushablePlayerIds.length > 0 && (
              <button
                type="button"
                onClick={selectAllPushable}
                className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              >
                Select all with push
              </button>
            )
          )}
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter roster…"
          className="min-w-0 flex-1 rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={() => setShowOnlyMissing((v) => !v)}
          className={
            'flex items-center gap-1 rounded border px-2 py-1.5 text-xs ' +
            (showOnlyMissing
              ? 'border-indigo-700 bg-indigo-900/40 text-indigo-200'
              : 'border-slate-700 text-slate-300 hover:bg-slate-800')
          }
        >
          Without link only
        </button>
      </div>

      {(linksQuery.isLoading || playersQuery.isLoading) && (
        <div className="text-sm text-slate-400">Loading…</div>
      )}
      {!linksQuery.isLoading && !playersQuery.isLoading && filteredRows.length === 0 && (
        <div className="text-sm text-slate-500">
          {showOnlyMissing
            ? 'Every active player has a magic link. Nice.'
            : 'No players match this filter.'}
        </div>
      )}

      <ul className="space-y-1.5">
        {filteredRows.map((row) => (
          <RosterLinkRow
            key={row.player.id}
            row={row}
            busy={busy}
            checked={selected.has(row.player.id)}
            onToggleSelect={() => toggleSelected(row.player.id)}
            onCreate={() => createLink.mutate(row.player.id)}
            onRotate={() => createLink.mutate(row.player.id)}
            onRevoke={() => {
              const label = row.player.displayName?.trim() || row.player.name;
              if (
                confirm(
                  `Revoke the magic link for ${label}? The old URL stops working immediately.`,
                )
              )
                deleteLink.mutate(row.player.id);
            }}
            onSendPush={() =>
              setPushTarget({
                kind: 'player',
                playerId: row.player.id,
                label: row.player.displayName?.trim() || row.player.name,
              })
            }
          />
        ))}
      </ul>
      {pushTarget && (
        <SendPushDialog
          target={pushTarget}
          onClose={() => setPushTarget(null)}
          onSent={() => {
            setPushTarget(null);
            clearSelection();
          }}
        />
      )}
    </section>
  );
}

function SendPushDialog({
  target,
  onClose,
  onSent,
}: {
  target:
    | { kind: 'player'; playerId: number; label: string }
    | { kind: 'bulk'; playerIds: number[] };
  onClose: () => void;
  onSent: () => void;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [silent, setSilent] = useState(false);
  const send = useMutation({
    mutationFn: () =>
      api.sendPush({
        playerIds:
          target.kind === 'player' ? [target.playerId] : target.playerIds,
        title: title.trim(),
        body: body.trim() || undefined,
        url: url.trim() || undefined,
        silent: silent || undefined,
      }),
    onSuccess: (res) => {
      alert(
        `Sent to ${res.devices} device${res.devices === 1 ? '' : 's'}.`,
      );
      onSent();
    },
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to send push'),
  });
  const audience =
    target.kind === 'player'
      ? target.label
      : `${target.playerIds.length} selected player${target.playerIds.length === 1 ? '' : 's'}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-sm rounded-lg border border-slate-800 bg-slate-950 p-4 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-100">
            Send push to {audience}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <label className="mb-2 block text-xs text-slate-400">
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            className="mt-1 block w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
            placeholder="e.g. Event in 30 min"
          />
        </label>
        <label className="mb-2 block text-xs text-slate-400">
          Body
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={500}
            rows={3}
            className="mt-1 block w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
            placeholder="Optional message body"
          />
        </label>
        <label className="mb-3 block text-xs text-slate-400">
          Open URL on click
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            maxLength={500}
            className="mt-1 block w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
            placeholder="/"
          />
        </label>
        <label className="mb-3 flex items-center gap-2 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={silent}
            onChange={(e) => setSilent(e.target.checked)}
            className="h-4 w-4 accent-indigo-500"
          />
          Soft push (silent — no sound / vibration)
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={send.isPending || title.trim().length === 0}
            onClick={() => send.mutate()}
            className="flex items-center gap-1 rounded bg-indigo-600 px-3 py-1 text-xs text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            <Send size={12} /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

function RosterLinkRow({
  row,
  busy,
  checked,
  onToggleSelect,
  onCreate,
  onRotate,
  onRevoke,
  onSendPush,
}: {
  row: RosterRow;
  busy: boolean;
  checked: boolean;
  onToggleSelect: () => void;
  onCreate: () => void;
  onRotate: () => void;
  onRevoke: () => void;
  onSendPush: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const { player, link } = row;
  const name = player.displayName?.trim() || player.name;
  const url = link ? playerHubUrl(link.token) : null;

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <li className="rounded border border-slate-800 bg-slate-950/40 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {link && link.pushDeviceCount > 0 && (
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggleSelect}
            className="h-4 w-4 shrink-0 accent-indigo-500"
            aria-label="Select for push"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 font-medium">
            <span>
              {name}{' '}
              <span className="text-xs text-slate-500">· {player.rank}</span>
            </span>
            {link ? (
              <>
                <span
                  className="inline-flex items-center gap-1 rounded bg-indigo-900/50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-200"
                  title="Magic link issued"
                >
                  <LinkIcon size={10} /> Link
                </span>
                <span
                  className="inline-flex items-center gap-1 rounded bg-slate-800/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-300"
                  title={
                    link.lastSubmissionAt
                      ? `Last submitted ${link.lastSubmissionAt.slice(0, 16).replace('T', ' ')}`
                      : 'No submissions yet'
                  }
                >
                  {link.submissionCount}× used
                </span>
              </>
            ) : (
              <span
                className="inline-flex items-center gap-1 rounded bg-slate-800/60 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-400"
                title="No magic link yet"
              >
                No link
              </span>
            )}
            {link && link.pushDeviceCount > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded bg-indigo-900/50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-200"
                title={`Player has ${link.pushDeviceCount} push device${link.pushDeviceCount === 1 ? '' : 's'} registered`}
              >
                <BellRing size={10} /> Push
              </span>
            )}
          </div>
          {url && (
            <div className="mt-1 flex items-center gap-2">
              <LinkIcon size={12} className="shrink-0 text-slate-500" />
              <code className="min-w-0 truncate text-xs text-slate-400">
                {url}
              </code>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {!link && (
            <button
              type="button"
              disabled={busy}
              onClick={onCreate}
              className="flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs hover:bg-indigo-500 disabled:opacity-50"
            >
              <Plus size={12} /> Create link
            </button>
          )}
          {link && url && (
            <>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(url);
                  setCopied(true);
                }}
                className="flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800"
              >
                <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
              </button>
              {link.pushDeviceCount > 0 && (
                <button
                  type="button"
                  onClick={onSendPush}
                  title="Send a push notification to this player"
                  className="flex items-center gap-1 rounded border border-indigo-700 bg-indigo-900/30 px-2 py-1 text-xs text-indigo-200 hover:bg-indigo-900/60"
                >
                  <Send size={12} /> Push
                </button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={onRotate}
                title="Rotate: creates a fresh link and invalidates the old one"
                className="flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs hover:bg-slate-800 disabled:opacity-50"
              >
                <RefreshCw size={12} /> Rotate
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={onRevoke}
                className="flex items-center gap-1 rounded border border-red-900/60 px-2 py-1 text-xs text-red-300 hover:bg-red-950/40 disabled:opacity-50"
              >
                <Trash2 size={12} /> Revoke
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function shortUa(ua: string | null): string {
  if (!ua) return '—';
  const m =
    ua.match(/\((iPhone|iPad|Android|Mac|Windows|Linux)[^)]*\)/) ??
    ua.match(/(Chrome|Firefox|Safari|Edg)\/[\d.]+/);
  return m ? m[0].replace(/[()]/g, '') : ua.slice(0, 40);
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Date.now() - then;
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function PlayerUsageSection() {
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const query = useQuery({
    queryKey: ['self-report-links'],
    queryFn: api.listSelfReportLinks,
  });
  const setAutoApprove = useMutation({
    mutationFn: (args: { playerId: number; autoApprove: boolean }) =>
      api.setSelfReportAutoApprove(args.playerId, args.autoApprove),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['self-report-links'] }),
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to change trust'),
  });
  const rows = useMemo(() => {
    const all = (query.data?.rows ?? []).slice().sort((a, b) => {
      if (b.submissionCount !== a.submissionCount)
        return b.submissionCount - a.submissionCount;
      const rankDiff = RANK_ORDER[b.rank] - RANK_ORDER[a.rank];
      if (rankDiff !== 0) return rankDiff;
      const nameA = (a.displayName?.trim() || a.name).toLowerCase();
      const nameB = (b.displayName?.trim() || b.name).toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return showAll ? all : all.filter((r) => r.submissionCount > 0 || r.autoApprove);
  }, [query.data, showAll]);
  const trustedCount = (query.data?.rows ?? []).filter((r) => r.autoApprove).length;
  const totalUsages = (query.data?.rows ?? []).reduce(
    (sum, r) => sum + r.submissionCount,
    0,
  );
  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          <ShieldCheck size={14} /> Players & usage
          <span className="ml-1 rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-300">
            {rows.length}
          </span>
        </h2>
        <span className="text-xs text-slate-500">
          {trustedCount} trusted · {totalUsages} self-report
          {totalUsages === 1 ? '' : 's'} total
        </span>
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className={
            'ml-auto rounded border px-2 py-1 text-xs ' +
            (showAll
              ? 'border-indigo-700 bg-indigo-900/40 text-indigo-200'
              : 'border-slate-700 text-slate-300 hover:bg-slate-800')
          }
        >
          {showAll ? 'Hide unused' : 'Show all'}
        </button>
      </div>
      {query.isLoading && (
        <div className="text-sm text-slate-400">Loading…</div>
      )}
      {!query.isLoading && rows.length === 0 && (
        <div className="text-sm text-slate-500">
          No players with magic links yet. Create some in Player access.
        </div>
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-slate-800/80">
          {rows.map((r) => {
            const label = r.displayName?.trim() || r.name;
            return (
              <li key={r.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="truncate font-medium">{label}</span>
                  <span className="text-xs text-slate-500">· {r.rank}</span>
                  {r.autoApprove && (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-emerald-900/50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-200"
                      title="Auto-approved self-reports skip the pending queue"
                    >
                      <ShieldCheck size={10} /> Trusted
                    </span>
                  )}
                </span>
                <span
                  className="tabular-nums text-xs text-slate-400"
                  title={
                    r.lastSubmissionAt
                      ? `Last: ${r.lastSubmissionAt.slice(0, 16).replace('T', ' ')}`
                      : 'No submissions yet'
                  }
                >
                  {r.submissionCount}× used
                </span>
                <button
                  type="button"
                  disabled={setAutoApprove.isPending}
                  onClick={() => {
                    const msg = r.autoApprove
                      ? `Require review again for ${label}'s self-reports?`
                      : `Trust ${label} to submit self-reports without admin review?`;
                    if (confirm(msg))
                      setAutoApprove.mutate({
                        playerId: r.id,
                        autoApprove: !r.autoApprove,
                      });
                  }}
                  className={
                    'flex items-center gap-1 rounded border px-2 py-1 text-xs disabled:opacity-50 ' +
                    (r.autoApprove
                      ? 'border-emerald-900/60 text-emerald-300 hover:bg-emerald-950/40'
                      : 'border-slate-700 text-slate-300 hover:bg-slate-800')
                  }
                >
                  {r.autoApprove ? (
                    <>
                      <ShieldOff size={12} /> Untrust
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={12} /> Trust
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function PushSubscribersSection() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['push-subscribers'],
    queryFn: api.listPushSubscribers,
    refetchInterval: 30_000,
  });
  const testMutation = useMutation({
    mutationFn: (userId: number) => api.sendPushTest(userId),
    onSuccess: (res) => {
      alert(`Test push sent to ${res.sentTo} device(s).`);
      qc.invalidateQueries({ queryKey: ['push-subscribers'] });
    },
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : 'Failed to send test push'),
  });
  const rows = query.data?.rows ?? [];
  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          <Smartphone size={14} /> Push devices
          <span className="ml-1 rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-300">
            {rows.length}
          </span>
        </h2>
        <p className="ml-auto text-xs text-slate-500">
          Admins who receive push notifications on at least one device.
        </p>
      </div>
      {query.isLoading && (
        <div className="text-sm text-slate-400">Loading…</div>
      )}
      {!query.isLoading && rows.length === 0 && (
        <div className="text-sm text-slate-500">
          No user has enabled push notifications yet.
        </div>
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-slate-800/80">
          {rows.map((row) => (
            <PushSubscriberRowItem
              key={row.userId}
              row={row}
              busy={testMutation.isPending}
              onTest={() => testMutation.mutate(row.userId)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function PushSubscriberRowItem({
  row,
  busy,
  onTest,
}: {
  row: PushSubscriberRow;
  busy: boolean;
  onTest: () => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-3 py-2">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{row.username}</span>
          <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
            {row.role}
          </span>
          {!row.active && (
            <span className="rounded bg-rose-900/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-200">
              inactive
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {row.deviceCount} device{row.deviceCount === 1 ? '' : 's'} ·{' '}
          {shortUa(row.lastUa)} · last used {formatRelative(row.lastUsedAt)}
        </div>
      </div>
      <button
        type="button"
        disabled={busy}
        onClick={onTest}
        className="flex items-center gap-1 rounded border border-indigo-700 bg-indigo-900/30 px-2 py-1 text-xs text-indigo-200 hover:bg-indigo-900/60 disabled:opacity-50"
      >
        <Send size={12} /> Send test
      </button>
    </li>
  );
}
